import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskProgressField from '../timer/TaskProgressField';
import { elapsedHm, type TimerCloseRow } from './endOfShiftModel';

/**
 * P-10e (D-85) — the running timers of the break / clock-out window, one row
 * each: ☑ (checked by default), the task, the time, "מה עשיתי" (optional) and,
 * for a task, its status circles with the progress bar under them (D-82).
 * Quiet: hairline rows, no cards, the details only for a checked row.
 */
export default function TimersCloseList({ rows, onChange, disabled = false }: {
  rows: TimerCloseRow[];
  onChange: (rows: TimerCloseRow[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const update = (timerId: string, patch: Partial<TimerCloseRow>) =>
    onChange(rows.map((r) => (r.timerId === timerId ? { ...r, ...patch } : r)));

  return (
    <ul className="divide-y divide-border border-y border-border" data-timers-close-list="">
      {rows.map((r) => {
        const checkId = `tcl-${r.timerId}`;
        const noteId = `tcl-note-${r.timerId}`;
        return (
          <li key={r.timerId} className="py-2.5" data-timer-row={r.timerId}>
            <div className="flex items-center gap-2.5">
              <input
                id={checkId}
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-primary-700"
                checked={r.checked}
                disabled={disabled}
                onChange={(e) => update(r.timerId, { checked: e.target.checked })}
                aria-label={t('timer.closeList.stopThis', { title: r.title })}
              />
              <label htmlFor={checkId} className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium text-text">
                <bdi>{r.title}</bdi>
              </label>
              <span className="shrink-0 text-xs tabular-nums text-text-2" dir="ltr">{elapsedHm(r.startedAt, now)}</span>
            </div>
            {r.checked && (
              <div className="mt-2 space-y-2 ps-6">
                <div>
                  <label htmlFor={noteId} className="mb-0.5 block text-xs text-text-2">
                    {t('timer.closeList.whatIDid')} <span className="text-text-3">({t('timer.closeList.optional')})</span>
                  </label>
                  <input
                    id={noteId}
                    type="text"
                    className="input h-control-sm w-full text-sm"
                    value={r.summary}
                    disabled={disabled}
                    maxLength={2000}
                    onChange={(e) => update(r.timerId, { summary: e.target.value })}
                  />
                </div>
                {r.isTask && (
                  <TaskProgressField
                    value={r.value}
                    size="sm"
                    disabled={disabled}
                    title={r.title}
                    onChange={(value) => update(r.timerId, { value })}
                  />
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
